import type { JobListItem } from '../types/jobs';
import type { JobStatus } from '../types/monitoring';

// A running/stuck eval-set older than this is "long-running" — the thing we
// want to surface so an old job nobody noticed doesn't keep burning compute.
const LONG_RUNNING_MS = 24 * 60 * 60 * 1000;

export function isLongRunning(item: JobListItem): boolean {
  if (item.status !== 'running' && item.status !== 'stuck') return false;
  const age = Date.now() - new Date(item.created_at).getTime();
  return Number.isFinite(age) && age > LONG_RUNNING_MS;
}

export function labelForStatus(status: JobStatus): string {
  switch (status) {
    case 'pending':
      return 'Queued';
    case 'running':
      return 'Running';
    case 'waiting_for_capacity':
      return 'Waiting for capacity';
    case 'importing':
      return 'Importing samples';
    case 'stuck':
      return 'Stuck';
    case 'complete':
      return 'Complete';
    case 'failed':
      return 'Failed';
    case 'deleted':
      return 'Deleted';
  }
}

export function dotClassForStatus(status: JobStatus): string {
  switch (status) {
    case 'pending':
      return 'bg-amber-500 dark:bg-amber-400';
    case 'running':
    case 'importing':
    case 'waiting_for_capacity':
      return 'bg-blue-500 dark:bg-blue-400 animate-pulse';
    case 'stuck':
      return 'bg-amber-500 dark:bg-amber-400 animate-pulse';
    case 'complete':
      return 'bg-brand-600 dark:bg-brand-300';
    case 'failed':
      return 'bg-red-600 dark:bg-red-400';
    case 'deleted':
      return 'bg-gray-400 dark:bg-gray-500';
  }
}

export function friendlyTroubleReason(reason: string | null): string | null {
  if (reason === null) return null;
  switch (reason) {
    case 'OOMKilled':
      return 'runner out of memory';
    case 'ImagePullBackOff':
    case 'ErrImagePull':
      return 'image pull failing';
    case 'CrashLoopBackOff':
      return 'container crash looping';
    case 'CreateContainerError':
    case 'CreateContainerConfigError':
      return 'container creation failed';
    case 'Unschedulable':
      return 'waiting for cluster capacity';
    default:
      return reason;
  }
}
